/**
 * TelemetryBlocks — niv_clinic_usage facts for the details tab, split in two
 * sections so the jump bar can target them:
 *   TelemetryUsageSection    — recompute header · logins · integrations · meetings
 *   TelemetryFeaturesSection — feature tips · pages · feature usage · per-therapist
 * Both take the `usage` doc the page already fetched (getClinicTelemetryDetail).
 *
 * ntl-* classes come from analytics/NivTelemetryTab.css.
 */

import {
  useGetClinicFeatureTipsQuery,
  useSetClinicFeatureTipVisibilityMutation,
  useRecomputeTelemetryScoresMutation,
} from "../../../../../store/services/nivApi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  PAGE_LABELS,
  FEATURE_LABELS,
  DEVICE_LABELS,
  pageLabel,
  featureLabel,
  featureStateLabel,
  formatComputedAt,
  pendingUpdateTone,
} from "../../analytics/telemetryLabels";
import { TelemetryTooltip } from "../../analytics/telemetryChartUtils";
import "../../analytics/NivTelemetryTab.css";
import { useToast } from "../../../../../contexts/ToastContext";
import {
  CalendarCheck,
  CalendarPlus,
  ChartLineUp,
  SignIn,
  Users,
  Info,
  Plug,
  ArrowsClockwise,
  Sparkle,
  Lightbulb,
  Eye,
  EyeSlash,
} from "@phosphor-icons/react";

const normalizeTopPage = (item) => {
  if (!item) return null;
  if (typeof item === "string") return { key: item, count: null };
  return { key: item.key || item.page || item.pageKey || "", count: item.count ?? null };
};

// ============================================
// USAGE PANEL PRIMITIVES
// ============================================

/** Hebrew label for "how long ago", from a day count. */
const relativeDaysLabel = (days) => {
  if (days == null) return "מעולם לא";
  const n = Number(days);
  if (!Number.isFinite(n)) return "—";
  if (n <= 0) return "היום";
  if (n === 1) return "אתמול";
  return `לפני ${n} ימים`;
};

/** Inactivity tone — mirrors the health-card thresholds (7 / 30 days). */
const loginTone = (days) => {
  if (days == null) return "danger";
  const n = Number(days);
  if (!Number.isFinite(n)) return null;
  if (n >= 30) return "danger";
  if (n >= 7) return "warn";
  return "ok";
};

const UsageKpi = ({ label, value, sub, tone }) => (
  <div className={`ntl-u-kpi${tone ? ` ntl-u-kpi--${tone}` : ""}`}>
    <span className="ntl-u-kpi-value">{value}</span>
    <span className="ntl-u-kpi-label">{label}</span>
    {sub && <span className="ntl-u-kpi-sub">{sub}</span>}
  </div>
);

const ConnChip = ({
  label,
  connected,
  detail,
  connectedLabel = "מחובר",
  idleLabel = "לא מחובר",
}) => (
  <div className={`ntl-u-conn${connected ? " is-on" : ""}`}>
    <span className="ntl-u-conn-name">{label}</span>
    <span className="ntl-u-conn-state">{connected ? connectedLabel : idleLabel}</span>
    {detail && <span className="ntl-u-conn-detail">{detail}</span>}
  </div>
);

// Past-window statuses, in reading order. "scheduled" in the past means the
// meeting happened (or didn't) and nobody ever updated it.
const PAST_STATUS_LABELS = [
  ["completed", "הושלמו", "ok"],
  ["cancelled", "בוטלו", "warn"],
  ["scheduled", "לא עודכנו", "danger"],
];

const PastWindow = ({ title, data }) => {
  const total = Number(data?.total) || 0;
  return (
    <div className="ntl-u-window">
      <div className="ntl-u-window-head">
        <span className="ntl-u-window-title">{title}</span>
        <span className="ntl-u-window-total">{total} פגישות</span>
      </div>
      {total === 0 ? (
        <div className="ntl-u-window-empty">אין פגישות בתקופה זו</div>
      ) : (
        <>
          <div className="ntl-u-bar">
            {PAST_STATUS_LABELS.map(([key, , tone]) => {
              const n = Number(data?.[key]) || 0;
              if (n <= 0) return null;
              return (
                <span
                  key={key}
                  className={`ntl-u-bar-seg ntl-u-bar-seg--${tone}`}
                  style={{ width: `${(n / total) * 100}%` }}
                />
              );
            })}
          </div>
          <div className="ntl-u-badges">
            {PAST_STATUS_LABELS.map(([key, label, tone]) => (
              <span key={key} className={`ntl-u-badge ntl-u-badge--${tone}`}>
                {label} {Number(data?.[key]) || 0}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FutureWindow = ({ title, data }) => {
  const scheduled = Number(data?.scheduled) || 0;
  const cancelled = Number(data?.cancelled) || 0;
  return (
    <div className="ntl-u-window">
      <div className="ntl-u-window-head">
        <span className="ntl-u-window-title">{title}</span>
        <span
          className={`ntl-u-window-total${scheduled === 0 ? " is-empty" : ""}`}
        >
          {scheduled} מתוכננות
        </span>
      </div>
      {cancelled > 0 && (
        <div className="ntl-u-badges">
          <span className="ntl-u-badge ntl-u-badge--warn">בוטלו {cancelled}</span>
        </div>
      )}
    </div>
  );
};

function FeatureTipsAdminCard({ clinicId }) {
  const { showSuccess, showError } = useToast();
  const { data: tipsDoc } = useGetClinicFeatureTipsQuery(clinicId, {
    skip: !clinicId,
  });
  const [setTipVisibility, { isLoading: isToggling }] =
    useSetClinicFeatureTipVisibilityMutation();

  const tips = [...(tipsDoc?.tips || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );
  if (tips.length === 0) return null;
  const visibleMap = tipsDoc?.visible || {};
  // Clinics created after the nudge engine shipped decide by themselves; for
  // older clinics `visible` is still the only way a tip ever appears.
  const isAutoClinic = tipsDoc?.nudges?.autoEnabled === true;
  const eligibleCounts = {};
  for (const perTherapist of Object.values(
    tipsDoc?.nudges?.byTherapist || {}
  )) {
    for (const featureKey of Object.keys(perTherapist?.eligible || {})) {
      eligibleCounts[featureKey] = (eligibleCounts[featureKey] || 0) + 1;
    }
  }

  // null = automatic (the nightly nudge engine decides)
  const OVERRIDE_TOAST = {
    true: "הטיפ יוצג לכל המטפלים בקליניקה",
    false: "הטיפ לא יוצג למטפלים",
    null: "הטיפ חזר למצב אוטומטי",
  };

  const handleSetOverride = async (featureKey, nextVisible) => {
    const result = await setTipVisibility({
      clinicId,
      featureKey,
      visible: nextVisible,
    });
    if (result.error) {
      showError("שגיאה בעדכון הצגת הטיפ");
    } else {
      showSuccess(OVERRIDE_TOAST[String(nextVisible)]);
    }
  };

  return (
    <section className="ntl-card">
      <h3 className="ntl-card-title">
        <Lightbulb size={15} />
        טיפים למטפלים
      </h3>
      <p className="ntl-ftips-hint">
        {isAutoClinic
          ? "בקליניקה הזו הטיפים נשלחים אוטומטית לפי ותק וחוסר שימוש. ״הצג״ / ״הסתר״ עוקפים את ההחלטה האוטומטית."
          : "טיפים מוצגים למטפלים רק אחרי לחיצה על ״הצג״. מסומנים בכוכב — מומלצים לפי נתוני השימוש."}
      </p>
      <ul className="ntl-ftips-list">
        {tips.map((tip) => {
          const override = visibleMap[tip.featureKey];
          const isVisible = override === true;
          const isHidden = override === false;
          const isAuto = override === undefined || override === null;
          const eligibleCount = eligibleCounts[tip.featureKey] || 0;
          return (
            <li key={tip.featureKey} className="ntl-ftip">
              <span className="ntl-ftip-label">
                {featureLabel(tip.featureKey)}
              </span>
              {tip.suggested && (
                <span className="ntl-tip-badge">
                  <Sparkle size={10} weight="fill" /> מומלץ
                </span>
              )}
              {isVisible && (
                <span className="ntl-ftip-shown">מוצג לכולם</span>
              )}
              {isAutoClinic && isAuto && eligibleCount > 0 && (
                <span className="ntl-ftip-shown">
                  אוטומטי · {eligibleCount} מטפלים
                </span>
              )}
              <div className="ntl-ftip-actions">
                <button
                  type="button"
                  className={`ntl-ftip-toggle ${isVisible ? "is-visible" : ""}`}
                  disabled={isToggling}
                  onClick={() =>
                    handleSetOverride(tip.featureKey, isVisible ? null : true)
                  }
                >
                  <Eye size={13} /> הצג
                </button>
                {isAutoClinic && (
                  <button
                    type="button"
                    className={`ntl-ftip-toggle ${isHidden ? "is-visible" : ""}`}
                    disabled={isToggling}
                    onClick={() =>
                      handleSetOverride(tip.featureKey, isHidden ? null : false)
                    }
                  >
                    <EyeSlash size={13} /> הסתר
                  </button>
                )}
                {!isAutoClinic && isVisible && (
                  <button
                    type="button"
                    className="ntl-ftip-toggle"
                    disabled={isToggling}
                    onClick={() => handleSetOverride(tip.featureKey, false)}
                  >
                    <EyeSlash size={13} /> הסתר
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}


const buildTelemetryRows = (usage) => {
  const pageRows = Object.entries(usage?.pageViews30 || {})
    .map(([key, count]) => ({ key, label: pageLabel(key), count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const featureRows = Object.keys(FEATURE_LABELS)
    .map((key) => {
      const st = usage?.featureStatus?.[key] || null;
      return {
        key,
        label: FEATURE_LABELS[key],
        count: Number(usage?.features30?.[key]) || 0,
        count60: Number(usage?.features60?.[key]) || 0,
        state: st?.state || null,
        connected: st?.connected ?? null,
        source: st?.source || null,
        lastUsedDaysAgo: st?.lastUsedDaysAgo ?? null,
      };
    })
    .filter((f) => f.state !== "not_applicable");
  const therapists = Object.entries(usage?.perTherapist || {}).map(([uid, t]) => ({
    uid,
    name: t?.name || "מטפל ללא שם",
    sessions30: Number(t?.sessions30) || 0,
    activeDays30: Number(t?.activeDays30) || 0,
    topPages: (Array.isArray(t?.topPages) ? t.topPages : []).map(normalizeTopPage).filter((p) => p?.key),
    deviceMix: t?.deviceMix || {},
    pendingUpdate: Number(t?.pendingUpdate) || 0,
  }));
  return { pageRows, featureRows, therapists };
};

function RecomputeButton({ clinicId }) {
  const { showSuccess, showError } = useToast();
  const [recompute, { isLoading: recomputing }] = useRecomputeTelemetryScoresMutation();
  const handleRecompute = async () => {
    try {
      const res = await recompute({ clinicId }).unwrap();
      showSuccess(res?.written ? "הנתונים חושבו מחדש" : "אין עדיין נתוני שימוש לקליניקה זו");
    } catch {
      showError("שגיאה בחישוב מחדש של הנתונים");
    }
  };
  return (
    <button
      type="button"
      className="ntl-recompute-btn"
      onClick={handleRecompute}
      disabled={recomputing}
      title="חישוב מחדש כולל שימוש מהיום ומצב אינטגרציות"
    >
      <ArrowsClockwise size={15} className={recomputing ? "ntl-spinner" : ""} />
      {recomputing ? "מחשב…" : "חשב מחדש"}
    </button>
  );
}

export function TelemetryUsageSection({ clinicId, usage }) {
  const computedAtLabel = formatComputedAt(usage?.computedAt);
  const logins = usage?.logins || null;
  const integrations = usage?.integrations || null;
  const meetings = usage?.meetings || null;

  if (!usage) {
    return (
      <div className="ntl-detail">
        <div className="ntl-detail-empty">
          אין עדיין נתוני שימוש לקליניקה זו.
          <div style={{ marginTop: "var(--spacing-sm)" }}>
            <RecomputeButton clinicId={clinicId} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ntl-detail">
      <div className="ntl-header">
        <span className="ntl-note">
          <Info size={12} />
          {computedAtLabel ? `חושב לאחרונה ${computedAtLabel}` : "טרם חושב"} · חישוב לילי ב-02:45
        </span>
        <RecomputeButton clinicId={clinicId} />
      </div>
      <div className="ntl-detail-grid">
        {/* Logins */}
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <SignIn size={15} />
            כניסות למערכת
          </h3>
          <div className="ntl-u-kpis">
            <UsageKpi
              label="כניסה אחרונה"
              value={relativeDaysLabel(logins?.daysSinceLastLogin)}
              sub={logins?.lastLoginTherapistName || null}
              tone={loginTone(logins?.daysSinceLastLogin)}
            />
            <UsageKpi label="כניסות (30 יום)" value={logins?.sessions30d ?? "—"} />
            <UsageKpi label="ימים פעילים (30 יום)" value={logins?.activeDays30d ?? "—"} />
            <UsageKpi
              label="מטפלים פעילים"
              value={
                logins
                  ? `${logins.activeTherapists30d || 0}/${logins.totalTherapists || 0}`
                  : "—"
              }
              sub="ב-30 יום"
            />
          </div>
        </section>

        {/* Integrations & connections */}
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <Plug size={15} />
            חיבורים ואינטגרציות
          </h3>
          {!integrations ? (
            <div className="ntl-detail-empty">אין נתונים</div>
          ) : (
            <div className="ntl-u-conns">
              <ConnChip
                label="יומן Google"
                connected={integrations.googleCalendar?.connected}
                detail={
                  integrations.googleCalendar?.connected
                    ? `${integrations.googleCalendar.connectedTherapists}/${integrations.googleCalendar.totalTherapists} מטפלים`
                    : null
                }
              />
              <ConnChip
                label="Morning"
                connected={integrations.morning?.connected}
                detail={
                  integrations.morning?.documents30d
                    ? `${integrations.morning.documents30d} מסמכים ב-30 יום`
                    : null
                }
              />
              <ConnChip
                label="Grow תשלומים"
                connected={integrations.grow?.connected}
                detail={
                  integrations.grow?.links30d
                    ? `${integrations.grow.links30d} קישורים · ${integrations.grow.paid30d || 0} שולמו ב-30 יום`
                    : null
                }
              />
              <ConnChip
                label="קליטת מתעניינים"
                connected={integrations.leadsWebhook?.connected}
                detail={
                  integrations.leadsWebhook?.inbound30d
                    ? `${integrations.leadsWebhook.inbound30d} קליטות ב-30 יום`
                    : null
                }
              />
              <ConnChip
                label="חשבונית דיגיטלית"
                connected={integrations.digitalInvoice?.connected}
                detail={
                  integrations.digitalInvoice?.documents30d
                    ? `${integrations.digitalInvoice.documents30d} מסמכים ב-30 יום`
                    : null
                }
              />
              <ConnChip
                label="חדרים"
                connected={integrations.rooms?.configured}
                connectedLabel="מוגדר"
                idleLabel="לא מוגדר"
                detail={
                  integrations.rooms?.count ? `${integrations.rooms.count} חדרים` : null
                }
              />
              <ConnChip
                label="התראות פוש"
                connected={(integrations.push?.enabledTherapists || 0) > 0}
                connectedLabel="פעיל"
                idleLabel="כבוי"
                detail={
                  integrations.push
                    ? `${integrations.push.enabledTherapists}/${integrations.push.totalTherapists} מטפלים`
                    : null
                }
              />
              <ConnChip
                label="פגישות וידאו"
                connected={integrations.videoMeetings?.used}
                connectedLabel="בשימוש"
                idleLabel="לא בשימוש"
                detail={
                  integrations.videoMeetings?.minutesThisMonth
                    ? `${integrations.videoMeetings.minutesThisMonth} דק׳ החודש`
                    : null
                }
              />
              <ConnChip
                label="שאלונים"
                connected={(integrations.questionnaires?.links30d || 0) > 0}
                connectedLabel="בשימוש"
                idleLabel="לא בשימוש"
                detail={
                  integrations.questionnaires?.links30d
                    ? `${integrations.questionnaires.links30d} ב-30 יום`
                    : null
                }
              />
              <ConnChip
                label="חתימה דיגיטלית"
                connected={(integrations.digitalSignature?.links30d || 0) > 0}
                connectedLabel="בשימוש"
                idleLabel="לא בשימוש"
                detail={
                  integrations.digitalSignature?.links30d
                    ? `${integrations.digitalSignature.links30d} ב-30 יום`
                    : null
                }
              />
            </div>
          )}
        </section>

        {/* Meetings behind */}
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <CalendarCheck size={15} />
            פגישות — 7 ו-30 הימים האחרונים
          </h3>
          {!meetings?.past30d ? (
            <div className="ntl-detail-empty">אין נתונים</div>
          ) : (
            <>
              <PastWindow title="7 ימים אחרונים" data={meetings.past7d} />
              <PastWindow title="30 ימים אחרונים" data={meetings.past30d} />
              {meetings.pendingUpdateCount > 0 && (
                <div
                  className={`ntl-u-pending ntl-u-pending--${pendingUpdateTone(
                    meetings.pendingUpdateCount
                  )}`}
                  title="פגישות שהסתיימו ב-30 הימים האחרונים וממתינות לאישור / סיכום / תשלום"
                >
                  {meetings.pendingUpdateCount} ממתינות לעדכון
                </div>
              )}
            </>
          )}
        </section>

        {/* Meetings ahead */}
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <CalendarPlus size={15} />
            פגישות עתידיות
          </h3>
          {!meetings?.next30d ? (
            <div className="ntl-detail-empty">אין נתונים</div>
          ) : (
            <>
              <FutureWindow title="7 ימים קרובים" data={meetings.next7d} />
              <FutureWindow title="30 ימים קרובים" data={meetings.next30d} />
              {(meetings.next30d.scheduled || 0) === 0 && (
                <div className="ntl-u-warn">
                  אין פגישות מתוכננות בחודש הקרוב — סימן מובהק לנטישה
                </div>
              )}
            </>
          )}
        </section>

      </div>
    </div>
  );
}

export function TelemetryFeaturesSection({ clinicId, usage }) {
  const { pageRows, featureRows, therapists } = buildTelemetryRows(usage);
  const pageMax = pageRows[0]?.count || 1;
  const therapistChartData = therapists
    .slice()
    .sort((a, b) => b.sessions30 - a.sessions30)
    .map((t) => ({ name: t.name, sessions: t.sessions30 }));

  if (!usage) return null;

  return (
    <div className="ntl-detail">
      <div className="ntl-detail-grid">
        {/* Therapist-facing feature tips — NiV controls per-tip visibility */}
        <FeatureTipsAdminCard clinicId={clinicId} />

        {/* Page usage */}
        <section className="ntl-card">
          <h3 className="ntl-card-title">
            <ChartLineUp size={15} />
            עמודים בשימוש (30 יום)
          </h3>
          {pageRows.length === 0 ? (
            <div className="ntl-detail-empty">אין עדיין נתוני שימוש</div>
          ) : (
            <div className="ntl-hbars">
              {pageRows.map((p) => (
                <div key={p.key} className="ntl-hbar-row">
                  <span className="ntl-hbar-label">{p.label}</span>
                  <div className="ntl-hbar-track">
                    <div
                      className="ntl-hbar-fill"
                      style={{ width: `${Math.max(4, (p.count / pageMax) * 100)}%` }}
                    />
                  </div>
                  <span className="ntl-hbar-value">{p.count.toLocaleString("he-IL")}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Feature usage */}
        {usage && (
          <section className="ntl-card">
            <h3 className="ntl-card-title">
              <Sparkle size={15} />
              שימוש בפיצ'רים
            </h3>
            <div className="ntl-feature-list">
              {featureRows.map((f) => (
                <div key={f.key} className="ntl-feature-row" title={f.source === "server" ? "נמדד מהשרת (Firestore / אינטגרציה)" : f.source === "client" ? "נמדד מטלמטריית הלקוח" : undefined}>
                  <span className="ntl-feature-name">
                    {f.label}
                    {f.connected === true && (
                      <span className="ntl-feature-connected" title="אינטגרציה מחוברת">
                        {" "}<Plug size={12} weight="bold" />
                      </span>
                    )}
                  </span>
                  {f.count > 0 && (
                    <span className="ntl-feature-count" title="שימושים ב-30 יום">
                      {f.count}
                    </span>
                  )}
                  {f.state && (
                    <span className={`ntl-feature-state ntl-feature-state--${f.state}`}>
                      {f.state === "stale" && Number(f.lastUsedDaysAgo) > 0
                        ? `לא בשימוש ${Number(f.lastUsedDaysAgo)} ימים`
                        : featureStateLabel(f.state)}
                    </span>
                  )}
                  {f.source && (
                    <span className="ntl-feature-source">
                      {f.source === "server" ? "שרת" : "לקוח"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Per-therapist breakdown */}
        {therapists.length > 0 && (
          <section className="ntl-card">
            <h3 className="ntl-card-title">
              <Users size={15} />
              פילוח לפי מטפל (30 יום)
            </h3>
            {therapistChartData.length > 1 && (
              <div className="ntl-chart" dir="ltr">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={therapistChartData} margin={{ top: 20, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="var(--color-border-light)" />
                    <XAxis
                      dataKey="name"
                      tickLine={false}
                      axisLine={{ stroke: "var(--color-border)" }}
                      tick={{ fontSize: 11, fill: "var(--color-text-secondary)" }}
                      interval={0}
                    />
                    <YAxis
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                      tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
                      width={28}
                    />
                    <RechartsTooltip content={<TelemetryTooltip />} cursor={{ fill: "var(--color-background-alt)" }} />
                    <Bar dataKey="sessions" name="כניסות" fill="var(--color-primary-dark)" maxBarSize={28} radius={[4, 4, 0, 0]}>
                      <LabelList
                        dataKey="sessions"
                        position="top"
                        style={{ fontSize: 11, fontWeight: 600, fill: "var(--color-text-primary)" }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="ntl-therapists">
              {therapists.map((t) => {
                const mixTotal =
                  (Number(t.deviceMix.desktop) || 0) +
                  (Number(t.deviceMix.mobile) || 0) +
                  (Number(t.deviceMix.tablet) || 0);
                return (
                  <div key={t.uid} className="ntl-therapist-row">
                    <div className="ntl-therapist-head">
                      <span className="ntl-therapist-name">{t.name}</span>
                      <span className="ntl-therapist-meta">
                        {t.sessions30} כניסות · {t.activeDays30} ימים פעילים
                      </span>
                      {Number(t.pendingUpdate) > 0 && (
                        <span
                          className={`ntl-score-badge ntl-score-${pendingUpdateTone(t.pendingUpdate)}`}
                          title="פגישות שממתינות לעדכון"
                        >
                          {t.pendingUpdate} ממתינות לעדכון
                        </span>
                      )}
                      {mixTotal > 0 && (
                        <span
                          className="ntl-devicemix"
                          title={["desktop", "mobile", "tablet"]
                            .filter((k) => Number(t.deviceMix[k]) > 0)
                            .map(
                              (k) =>
                                `${DEVICE_LABELS[k]}: ${Math.round(
                                  ((Number(t.deviceMix[k]) || 0) / mixTotal) * 100
                                )}%`
                            )
                            .join(" · ")}
                        >
                          {["desktop", "mobile", "tablet"].map((k) => (
                            <span
                              key={k}
                              className={`ntl-devicemix-seg ntl-devicemix-${k}`}
                              style={{
                                width: `${((Number(t.deviceMix[k]) || 0) / mixTotal) * 100}%`,
                              }}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                    {t.topPages.length > 0 && (
                      <div className="ntl-chips">
                        {t.topPages.slice(0, 5).map((p) => (
                          <span key={p.key} className="ntl-chip">
                            {PAGE_LABELS[p.key] || p.key}
                            {p.count != null ? ` · ${p.count}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
